import React, {useState} from "react"
import {Formik} from "formik"
import {Badge} from "../Badge/Badge"
import Button from "../Button/Button"
import Admonition from "../Admonitions/Admonition"
import {Modal} from "../Modals/Modal"
import BuildPathDetails from "../BuildPathDetails/BuildPathDetails"
import {buildPathOptions, BuildPathType} from "../../pages/ChooseBuildPath/buildPathOptions"

interface SwitchBuildPathModeModalProps {
	currentMode: BuildPathType
	close: () => void
	onSwitch: (selectedMode: BuildPathType) => Promise<void>
}

export function SwitchBuildPathModeModal({currentMode, close, onSwitch}: SwitchBuildPathModeModalProps) {
	const [loading, setLoading] = useState(false)

	return (
		<Modal enableClickAway={false} close={close} classname="switch-build-path-modal-container">
			<div className="switch-build-path-modal">
				<h2 className="modal-title">Switch Build Path</h2>
				<Formik
					initialValues={{selectedMode: currentMode}}
					onSubmit={async (values) => {
						if (values.selectedMode !== currentMode) {
							setLoading(true)
							await onSwitch(values.selectedMode)
							setLoading(false)
						}
					}}
				>
					{({values, setFieldValue, handleSubmit}) => {
						const isSubmitDisabled = values.selectedMode === currentMode || loading

						return (
							<>
								<section className="modal-body">
									<form onSubmit={handleSubmit} noValidate>
										<div className="mode-options">
											{(Object.keys(buildPathOptions) as BuildPathType[]).map((type) => {
												const {title, subtitle, features} = buildPathOptions[type]
												const selected = values.selectedMode === type

												return (
													<div
														key={type}
														className={`mode-option ${selected ? "is-active" : ""}`}
														onClick={() => setFieldValue("selectedMode", type)}
													>
														{type === currentMode && <Badge text="Current Mode" className="option-badge" />}
														<label className="mode-option-header">
															<input
																type="radio"
																name="selectedMode"
																value={type}
																checked={selected}
																onChange={() => setFieldValue("selectedMode", type)}
															/>
															<span className="mode-option-title">{title}</span>
														</label>

														<BuildPathDetails subtitle={subtitle} features={features} />
													</div>
												)
											})}
										</div>

										<Admonition
											type="is-warning"
											title="Please Note"
											message={
												<>
													Changing your implementation mode impacts <b>every user in your team.</b>
												</>
											}
											isFullWidth
											className="admonition-note"
										/>

										<div className="action-buttons">
											<Button
												type="button"
												onClick={close}
												className="action-button"
												theme="neutral"
												variant="outline"
												rounded
												disabled={loading}
											>
												Cancel
											</Button>
											<Button
												type="submit"
												className="action-button"
												theme="neutral"
												rounded
												disabled={isSubmitDisabled}
												loading={loading}
											>
												Switch Mode
											</Button>
										</div>
									</form>
								</section>
							</>
						)
					}}
				</Formik>
			</div>
		</Modal>
	)
}
